'use client';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Dictionary, Notification } from '@witmetrics/utils';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import { isNewOrderNotification } from '@witmetrics/utils/notifications';
import { useActiveAccount, useDispatch } from '@witmetrics/data-store';
import {
  ACTIVE_ORDER_PARAMS,
  addRecentId,
  DEFAULT_DIALOGS,
  DEFAULT_OVERLAYS,
  type DialogName,
  DIALOGS,
  OVERLAYS,
} from '@witmetrics/unison-core';
import { showErrorToaster } from '@/utils/toasters';
import { usePath } from '@/hooks/usePath';
import { useSockets } from '@/providers/SocketsProvider';
import { useMountEffect } from '@/hooks/useMountEffect';
import { useId } from '@/hooks/useId';
import { useIds } from '@/hooks/useIds';
import { useDate } from '@/hooks/useDate';
import {
  getNotificationAccount,
  redirectToPlatformSelector,
  updateActiveAccount,
  updateReadNotification,
  useStoredAccountId,
  warnBeforeUnload,
} from './utils';

type AppStateValue = {
  activeOrderId: number | undefined;
  recentOrderIds: number[];
  activeCalendarDate: string | null;
  activeDialogs: Dictionary<boolean>;
  activeOverlays: Dictionary<boolean>;
  setActiveDialogs: Dispatch<SetStateAction<{ [p: string]: boolean }>>;
  onLogin: () => void;
  onApiError: (err: any, message: string, cb?: Function) => void;
  onClearActiveOrder: () => void;
  onClearRecentOrders: () => void;
  onSelectOrder: (orderId: number, params?: Dictionary<any>) => void;
  onStartTask: (date: string) => void;
  onOpenAgenda: (date: string) => void;
  onOpenNotification: (notification: Notification) => void;
  onToggleUnloadWarning: (activate: boolean) => void;
  onOpenDialog: (dialogName: DialogName) => void;
  onCloseDialog: (dialogName: DialogName) => void;
  onOpenOverlay: (overlayName: string) => void;
  onCloseOverlay: (overlayName: string) => void;
};

const AppStateContext = createContext<AppStateValue | undefined>(undefined);

export default function AppStateProvider({ children }: PropsWithChildren) {
  const activeOrderRef = useRef<number | undefined>();
  const loginCallbacks = useRef<Function[]>([]);
  const dispatch = useDispatch();
  const activeAccount = useActiveAccount();
  const { setApiErrorCallback } = useSockets();
  const { pushParams, removeParams } = usePath();
  const [activeOrderId, setActiveOrderId, { clear: clearActiveOrderId }] =
    useId();
  const [recentOrderIds, setRecentOrderIds, { clear: clearRecentOrderIds }] =
    useIds();
  const [activeCalendarDate, setActiveCalendarDate] = useDate();
  const [activeDialogs, setActiveDialogs] = useState(DEFAULT_DIALOGS);
  const [activeOverlays, setActiveOverlays] = useState(DEFAULT_OVERLAYS);
  const [storedAccountId, setStoredAccountId] = useStoredAccountId();

  useMountEffect(() => {
    setApiErrorCallback(handleSocketError);
  });

  useEffect(() => {
    if (activeAccount === null) {
      handleOpenDialog(DIALOGS.ACCOUNTS);
    } else if (activeAccount === undefined) {
      return;
    } else if (!activeAccount.portalOnly) {
      redirectToPlatformSelector();
    } else if (activeAccount.accountId !== storedAccountId) {
      handleResetAccountItems();
    }
  }, [activeAccount]);

  const handleResetAccountItems = () => {
    setActiveOrderId(activeOrderRef.current);
    activeOrderRef.current = undefined;
    clearRecentOrderIds();
    setStoredAccountId(activeAccount!.accountId);
  };

  const handleOpenDialog = (dialogName: DialogName) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: true }));
  };

  const handleCloseDialog = (dialogName: DialogName) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: false }));
  };

  const handleOpenOverlay = (overlayName: string) => {
    setActiveOverlays((overlays) => ({ ...overlays, [overlayName]: true }));
  };

  const handleCloseOverlay = (overlayName: string) => {
    setActiveOverlays((overlays) => ({ ...overlays, [overlayName]: false }));
    if (overlayName === OVERLAYS.AGENDA) setActiveCalendarDate(null);
  };

  const handleSocketError = () => {
    handleApiError(new UnauthorizedError('', ''), '');
  };

  const handleApiError = (err: any, toasterMessage: string, cb?: Function) => {
    if (err instanceof UnauthorizedError) {
      if (cb) loginCallbacks.current.push(cb);
      if (!activeDialogs[DIALOGS.LOGIN]) handleOpenDialog(DIALOGS.LOGIN);
    } else {
      showErrorToaster(toasterMessage);
      throw err;
    }
  };

  const handleSetActiveOrder = (
    orderId: number,
    params: Dictionary<any> = {}
  ) => {
    if (orderId === activeOrderId) return;
    pushParams({ ...params, activeOrderId: orderId });
    setActiveOrderId(orderId);
    setRecentOrderIds((ids) => addRecentId(ids, orderId));
  };

  const handleClearActiveOrder = () => {
    clearActiveOrderId();
    removeParams(ACTIVE_ORDER_PARAMS);
  };

  const handleStartTask = (date: string) => {
    setActiveCalendarDate(date);
    handleOpenDialog(DIALOGS.TASK);
  };

  const handleOpenAgenda = (date: string) => {
    setActiveCalendarDate(date);
    handleOpenOverlay(OVERLAYS.AGENDA);
  };

  const handleLogin = () => {
    loginCallbacks.current.forEach((cb) => cb());
    handleCloseDialog(DIALOGS.LOGIN);
    loginCallbacks.current = [];
  };

  const handleToggleUnloadWarning = (activate: boolean) => {
    window.removeEventListener('beforeunload', warnBeforeUnload);
    if (activate) window.addEventListener('beforeunload', warnBeforeUnload);
  };

  const handleOpenNotification = async (notification: Notification) => {
    if (!isNewOrderNotification(notification)) {
      showErrorToaster('Error opening notification');
      throw Error(
        `Unsupported notificationTypeId: ${notification.notificationTypeId}`
      );
    }
    const { accountType, accountId } = getNotificationAccount(notification);
    if (
      activeAccount?.accountType !== accountType ||
      activeAccount?.accountId !== accountId
    ) {
      const account = await updateActiveAccount(accountType, accountId);
      activeOrderRef.current = notification.content.orderId;
      dispatch.addActiveAccount(account);
    }
    if (!notification.isRead) updateReadNotification(notification.id);
    return handleSetActiveOrder(notification.content.orderId);
  };

  const contextValue = {
    activeOrderId,
    recentOrderIds,
    activeCalendarDate,
    activeDialogs,
    activeOverlays,
    setActiveDialogs,
    onLogin: handleLogin,
    onApiError: handleApiError,
    onClearActiveOrder: handleClearActiveOrder,
    onClearRecentOrders: clearRecentOrderIds,
    onSelectOrder: handleSetActiveOrder,
    onStartTask: handleStartTask,
    onOpenAgenda: handleOpenAgenda,
    onOpenNotification: handleOpenNotification,
    onToggleUnloadWarning: handleToggleUnloadWarning,
    onOpenDialog: handleOpenDialog,
    onCloseDialog: handleCloseDialog,
    onOpenOverlay: handleOpenOverlay,
    onCloseOverlay: handleCloseOverlay,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
